import { ReactNode } from "react"
import { createPortal } from "react-dom"

import {
  IconAlertOctagonFilled,
  IconAlertTriangleFilled,
  IconCircleCheckFilled,
  IconInfoCircleFilled,
  IconLoader,
  IconX,
} from "@tabler/icons-react"
import { Action, ExternalToast, toast as sonnerToast, Toaster } from "sonner"

import { cn } from "@/lib/utils"

import { Button } from "./button"

type ToastTypes = "success" | "error" | "message" | "caution" | "loading"
type ToastId = string | number

const COLOR_CLASSES: Record<ToastTypes, string> = {
  success: /*tw*/ "border-success bg-success/10 text-success",
  error: /*tw*/ "border-destructive bg-destructive/10 text-destructive",
  message: /*tw*/ "border-foreground bg-foreground/10 text-foreground",
  caution: /*tw*/ "border-warning bg-warning/10 text-warning",
  loading: /*tw*/ "border-foreground bg-foreground/10 text-foreground",
}

const TYPE_ICONS: Record<ToastTypes, typeof IconCircleCheckFilled> = {
  success: IconCircleCheckFilled,
  error: IconAlertOctagonFilled,
  message: IconInfoCircleFilled,
  caution: IconAlertTriangleFilled,
  loading: IconLoader,
}

function isAction(value: ExternalToast["action"]): value is Action {
  return typeof value === "object" && value !== null && "onClick" in value
}

export interface ToastProps {
  icon?: ExternalToast["icon"]
  children: ReactNode
  type?: ToastTypes
  className?: string
  bodyClassName?: string
  hideIcon?: boolean
  /** Either an `Action` (rendered as a styled button) or a `ReactNode` (rendered as-is). */
  action?: ExternalToast["action"]
  /**
   * Either an `Action` (the X close button is rendered; clicking it fires `onClick`),
   * or a `ReactNode` rendered in the close slot.
   */
  cancel?: ExternalToast["cancel"]
}

export function Toast({
  icon,
  children,
  type = "message",
  className,
  bodyClassName,
  hideIcon = false,
  action,
  cancel,
}: ToastProps) {
  const TypeIcon = TYPE_ICONS[type]

  return (
    <div className="rounded-md bg-card">
      <div
        className={cn(
          "flex gap-2 rounded-md border border-solid p-4",
          COLOR_CLASSES[type],
          className,
        )}
      >
        {!hideIcon
          ? icon || (
              <TypeIcon
                size={20}
                className={cn("shrink-0", {
                  "animate-spin": type === "loading",
                })}
              />
            )
          : null}

        <span className={cn("flex grow flex-col gap-2 text-body1", bodyClassName)}>
          {children}
          {action && (
            <div className="flex">
              {isAction(action) ? (
                <Button
                  variant="neutral"
                  size="small"
                  onClick={action.onClick}
                  style={action.actionButtonStyle}
                  className="whitespace-nowrap"
                >
                  {action.label}
                </Button>
              ) : (
                action
              )}
            </div>
          )}
        </span>

        {cancel ? (
          isAction(cancel) ? (
            <button
              type="button"
              aria-label={typeof cancel.label === "string" ? cancel.label : "Close"}
              onClick={cancel.onClick}
              style={cancel.actionButtonStyle}
              className="shrink-0 text-muted-foreground"
            >
              <IconX size={20} />
            </button>
          ) : (
            cancel
          )
        ) : null}
      </div>
    </div>
  )
}

export function ToastContainer() {
  return createPortal(
    <Toaster
      position="bottom-right"
      toastOptions={{ classNames: { toast: "right-0" } }}
      offset={16}
      gap={8}
      className="!z-300"
    />,
    document.body,
  )
}

type Renderable<A = ToastId> = ReactNode | ((arg: A) => ReactNode)

function resolve<A>(value: Renderable<A>, arg: A): ReactNode {
  return typeof value === "function" ? value(arg) : value
}

type ToastHandler = (message: Renderable, options?: ExternalToast) => ToastId

/**
 * - `undefined` (not passed): X is rendered and dismisses on click.
 * - `null`: no X is rendered.
 * - `Action`: X is rendered; clicking fires `onClick` then dismisses.
 * - `ReactNode`: rendered as-is in the close slot.
 */
function resolveCancel(
  id: ToastId,
  cancel: ExternalToast["cancel"],
): ExternalToast["cancel"] {
  if (cancel === null) return null
  if (cancel === undefined) {
    return { label: "Close", onClick: () => sonnerToast.dismiss(id) }
  }
  if (!isAction(cancel)) return cancel
  return {
    label: cancel.label,
    onClick: (event) => {
      cancel.onClick(event)
      sonnerToast.dismiss(id)
    },
    actionButtonStyle: cancel.actionButtonStyle,
  }
}

const createToastHandler =
  (type: ToastTypes): ToastHandler =>
  (message, options) => {
    const { cancel: userCancel, icon, action, ...sonnerOpts } = options ?? {}
    // Loading toasts track in-flight work, so they're not dismissable by default
    // — dismissing one wouldn't cancel the work, it'd just hide progress and look
    // stuck. We drop both the close button and swipe-to-dismiss (`dismissible`);
    // programmatic `toast.dismiss`/id reuse still clears them. A caller that
    // passes an explicit `cancel` does have a way to abort, so that case stays
    // dismissable. Callers can also force it via `dismissible: true`.
    const lockLoading = type === "loading" && userCancel === undefined
    const cancel = lockLoading ? null : userCancel
    const loadingDefaults = lockLoading
      ? { dismissible: false, duration: Infinity }
      : {}
    return sonnerToast.custom(
      (id) => (
        <Toast
          type={type}
          icon={icon}
          action={action}
          cancel={resolveCancel(id, cancel)}
        >
          {resolve(message, id)}
        </Toast>
      ),
      { duration: 7000, ...loadingDefaults, ...sonnerOpts },
    )
  }

const successToastHandler = createToastHandler("success")
const errorToastHandler = createToastHandler("error")
const messageToastHandler = createToastHandler("message")
const cautionToastHandler = createToastHandler("caution")
const loadingToastHandler = createToastHandler("loading")

type PromiseMessages<T> = {
  loading: ReactNode
  success: ReactNode | ((data: T) => ReactNode)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- callers often access `.message` on the error
  error: ReactNode | ((reason: any) => ReactNode)
}

type PromiseOptions = ExternalToast & {
  loading?: ExternalToast
  success?: ExternalToast
  error?: ExternalToast
}

function promise<T>(
  p: Promise<T>,
  msgs: PromiseMessages<T>,
  opts?: PromiseOptions,
  responseValidator?: (response: T) => string | null,
) {
  const {
    loading: loadingOpts,
    success: successOpts,
    error: errorOpts,
    ...baseOpts
  } = opts ?? {}
  const loadingId = loadingToastHandler(msgs.loading, { ...baseOpts, ...loadingOpts })
  const showError = (reason: unknown) => {
    sonnerToast.dismiss(loadingId)
    errorToastHandler(resolve(msgs.error, reason), { ...baseOpts, ...errorOpts })
  }

  p.then((data) => {
    const errorMsg = responseValidator?.(data) ?? null
    if (errorMsg) {
      showError(errorMsg)
    } else {
      sonnerToast.dismiss(loadingId)
      successToastHandler(resolve(msgs.success, data), {
        ...baseOpts,
        ...successOpts,
      })
    }
  }).catch(showError)

  return p
}

// Minimal toast config for admin.
function lazy<T>(p: Promise<T>, responseValidator?: (response: T) => string | null) {
  return promise(
    p,
    {
      loading: "submitting…",
      success: "success!",
      error: (err) => `Error: ${err}`,
    },
    {},
    responseValidator,
  )
}

/**
 * Custom toast singleton. Use this instead of importing `toast` from `sonner`
 * directly — it adds typed variants (`message`, `caution`), `action`/`cancel`
 * options, and `promise`/`lazy` helpers wired to our `Toast` visual component.
 */
export const toast = {
  success: successToastHandler,
  error: errorToastHandler,
  message: messageToastHandler,
  caution: cautionToastHandler,
  loading: loadingToastHandler,
  promise,
  lazy,
  dismiss: sonnerToast.dismiss,
  custom: sonnerToast.custom,
  /** The toasts currently on screen, oldest first. */
  getToasts: sonnerToast.getToasts,
}
